import { User } from "../types/userTypes";
import api from "../config/Api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"; 

export const signin = createAsyncThunk<any, { loginRequest: any; navigate: (path: string) => void }>(
    "/auth/signin",
    async ({ loginRequest, navigate }, { rejectWithValue }) => {
        try {
            const response = await api.post("/auth/signing", loginRequest); 
            //console.log("login success ", response);
            if (response.data?.jwt) {
                localStorage.setItem("jwt", response.data.jwt);
                navigate("/"); 
            }
            return response.data; 
        } catch (error: any) {
           // console.log("error ---", error);
            const message = error.response?.data?.message || error.message || "Signin failed";
            return rejectWithValue(typeof message === 'string' ? message : JSON.stringify(message));
        }
    }
);

export const adminLogin = createAsyncThunk<any, { loginRequest: any; navigate: (path: string) => void }>(
    "/auth/adminLogin",
    async ({ loginRequest, navigate }, { rejectWithValue }) => {
        try {
            const response = await api.post("/auth/signing", loginRequest); 
            //console.log("Admin login success ", response);
            if (response.data?.jwt) {
                localStorage.setItem("jwt", response.data.jwt);
                navigate("/admin"); 
            }
            return response.data; 
        } catch (error: any) {
            //console.log("error ---", error);
            const message = error.response?.data?.message || error.message || "Admin login failed";
            return rejectWithValue(typeof message === 'string' ? message : JSON.stringify(message));
        }
    }
);

export const signup = createAsyncThunk<any, { signupRequest: any; navigate: (path: string) => void }>(
    "/auth/signup",
    async ({ signupRequest, navigate }, { rejectWithValue }) => {
        try {
            const response = await api.post("/auth/signup", signupRequest);
            //console.log("Signup success ", response);
            if (response.data?.jwt) {
                localStorage.setItem("jwt", response.data.jwt);
                navigate("/");
            }
            return response.data;
        } catch (error: any) {
            //console.log("error ---", error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const loginWithGoogle = createAsyncThunk<any, { idToken: string; navigate: (path: string) => void }>(
    "/auth/loginWithGoogle",
    async ({ idToken, navigate }, { rejectWithValue }) => {
        try {
            const response = await api.post("/auth/google", { token: idToken });
            //console.log("Google login success ", response);
            if (response.data?.jwt) {
                localStorage.setItem("jwt", response.data.jwt);
                navigate("/");
            }
            return response.data;
        } catch (error: any) {
            //console.log("Google login error ---", error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchUserProfile = createAsyncThunk<any, { jwt: string | null } | undefined>(
    "/auth/fetchUserProfile",
    async (params, { rejectWithValue }) => {
        try {
            const token = params?.jwt || localStorage.getItem("jwt");
            
            if (!token || token === "undefined" || token === "null") {
                return rejectWithValue("No valid JWT found");
            }

            const response = await api.get("/api/users/profile", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            //console.log("User profile ", response.data);
            return response.data;
        } catch (error: any) {
            //console.log("error ---", error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const logout = createAsyncThunk<any, any>(
    "/auth/logout",
    async (navigate, { rejectWithValue }) => {
        try {
            localStorage.clear();
            //console.log("logout success");
            if (navigate) navigate("/");
        } catch (error: any) {
            //console.log("error: -- " + error);
            return rejectWithValue(error.message);
        }
    }
);

interface AuthState {
    jwt: string | null;
    isLoggedIn: boolean;
    user: User | null;
    loading: boolean;
    error: string | null;
    role: string | null;
}

const getInitialJwt = (): string | null => {
    const jwt = localStorage.getItem("jwt");
    return jwt && jwt !== "undefined" && jwt !== "null" ? jwt : null;
};

const initialState: AuthState = {
    jwt: getInitialJwt(), 
    isLoggedIn: !!getInitialJwt(),
    user: null,
    loading: false,
    error: null,
    role: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        resetAuthState: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Signin Cases
        builder.addCase(signin.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(signin.fulfilled, (state, action) => {
            state.jwt = action.payload?.jwt || (typeof action.payload === 'string' ? action.payload : null);
            state.role = action.payload?.role || null;
            state.isLoggedIn = true;
            state.loading = false;
        });
        builder.addCase(signin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Admin Login Cases
        builder.addCase(adminLogin.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(adminLogin.fulfilled, (state, action) => {
            state.jwt = action.payload?.jwt || (typeof action.payload === 'string' ? action.payload : null);
            state.role = action.payload?.role || "ROLE_ADMIN";
            state.isLoggedIn = true;
            state.loading = false;
        });
        builder.addCase(adminLogin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Signup Cases
        builder.addCase(signup.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(signup.fulfilled, (state, action) => {
            state.jwt = action.payload?.jwt || (typeof action.payload === 'string' ? action.payload : null);
            state.role = action.payload?.role || null;
            state.isLoggedIn = true;
            state.loading = false;
        });
        builder.addCase(signup.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Google Login Cases
        builder.addCase(loginWithGoogle.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(loginWithGoogle.fulfilled, (state, action) => {
            state.jwt = action.payload?.jwt || (typeof action.payload === 'string' ? action.payload : null);
            state.role = action.payload?.role || null;
            state.isLoggedIn = true;
            state.loading = false;
        });
        builder.addCase(loginWithGoogle.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Profile Cases
        builder.addCase(fetchUserProfile.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
            state.loading = false;
            state.user = {
                ...state.user,
                ...action.payload,
                addresses: (action.payload?.addresses && action.payload.addresses.length > 0)
                    ? action.payload.addresses
                    : (state.user?.addresses || [])
            }; 
            state.role = action.payload?.role || state.role;
            state.isLoggedIn = true;
        });
        builder.addCase(fetchUserProfile.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Logout Cases
        builder.addCase(logout.fulfilled, (state) => {
            state.jwt = null;
            state.isLoggedIn = false;
            state.user = null;
            state.role = null;
            state.error = null;
            state.loading = false;
        });
    }
});

export const { resetAuthState } = authSlice.actions;
export default authSlice.reducer;